import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

function SheetOverlay({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  )
}

const sheetVariants = {
  top: 'inset-x-0 top-0 border-b',
  bottom: 'inset-x-0 bottom-0 border-t',
  left: 'inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
  right: 'inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
};

type SheetSide = keyof typeof sheetVariants;

function SheetContent({
  side = 'right',
  className,
  overlayClassName,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
    side?: SheetSide;
    /** Classes do fundo escurecido (ex.: `bg-transparent` quando o que está por trás tem de continuar à vista). */
    overlayClassName?: string;
  }) {
  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 flex flex-col bg-background shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-300 data-[state=closed]:duration-200',
          sheetVariants[side],
          {
            top: 'data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
            bottom: 'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
            left: 'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
            right: 'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
          }[side],
          className
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/6 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-primary/10 disabled:pointer-events-none">
          <X className="h-4 w-4" strokeWidth={1.75} />
          <span className="sr-only">Fechar</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
SheetHeader.displayName = 'SheetHeader';

/**
 * Corpo do painel — a única zona que rola, já com o padding da casa.
 *
 * Usa-se com `SheetContent className="p-0"`: o cabeçalho fica fixo (`shrink-0`)
 * e só isto rola. Sem isto, rolar uma lista longa levava o título consigo e
 * deixava de se saber o que se estava a ver.
 */
const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-5', className)} {...props} />
);
SheetBody.displayName = 'SheetBody';

/**
 * Eyebrow do painel: o rótulo minúsculo em maiúsculas que diz DE QUE tipo é a
 * coisa que se abriu ("LOTE", "ORDEM DE ACABAMENTO"). Sem ele, um código como
 * `9139` aparece nu e não se percebe o que é.
 */
const SheetEyebrow = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cn(
      'mb-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground',
      className
    )}
    {...props}
  />
);
SheetEyebrow.displayName = 'SheetEyebrow';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
SheetFooter.displayName = 'SheetFooter';

function SheetTitle({ className, ref, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      ref={ref}
      className={cn('text-lg font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetEyebrow,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};

